import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Send } from "lucide-react";
import { toast } from "sonner";
import EmailChipsInput from "./EmailChipsInput";

export default function ComposeDialog({ open, onOpenChange, onSent }) {
  const [inboxes, setInboxes] = useState([]);
  const [inboxId, setInboxId] = useState("");
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/inboxes");
        const live = data.filter((i) => !i.mock_mode);
        setInboxes(live);
        if (live.length && !inboxId) setInboxId(live[0].id);
      } catch (e) { void e; }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setTo([]); setCc([]); setBcc([]); setSubject(""); setBody("");
    setShowCc(false); setShowBcc(false);
  };

  const send = async () => {
    if (to.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    if (!inboxId) {
      toast.error("Connect a Gmail inbox first (Admin → Inboxes)");
      return;
    }
    setSending(true);
    try {
      await api.post("/compose", {
        inbox_id: inboxId,
        to,
        cc,
        bcc,
        subject: subject || "(no subject)",
        body_text: body,
      });
      toast.success("Email sent");
      reset();
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[95vh] overflow-y-auto" data-testid="compose-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New message</DialogTitle>
          <DialogDescription>
            Send a fresh email through your connected Gmail inbox. Type a name to autocomplete teammates and known contacts.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">From inbox</Label>
            {inboxes.length === 0 ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-1">
                No live Gmail inbox connected. Go to Admin → Inboxes → Connect Gmail first.
              </p>
            ) : (
              <Select value={inboxId} onValueChange={setInboxId}>
                <SelectTrigger className="mt-1" data-testid="compose-inbox-select"><SelectValue placeholder="Pick an inbox" /></SelectTrigger>
                <SelectContent>
                  {inboxes.map((i) => (
                    <SelectItem key={i.id} value={i.id}>{i.email_address}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <Label className="text-xs">To</Label>
              <div className="flex items-center gap-2 text-[11px]">
                {!showCc && (
                  <button type="button" onClick={() => setShowCc(true)} className="text-zinc-500 hover:text-[#0033CC]" data-testid="compose-add-cc">
                    + Cc
                  </button>
                )}
                {!showBcc && (
                  <button type="button" onClick={() => setShowBcc(true)} className="text-zinc-500 hover:text-[#0033CC]" data-testid="compose-add-bcc">
                    + Bcc
                  </button>
                )}
              </div>
            </div>
            <div className="mt-1">
              <EmailChipsInput
                value={to}
                onChange={setTo}
                placeholder="Start typing a name or email — Hailey, fox@…"
                testIdPrefix="compose-to"
              />
            </div>
          </div>
          {showCc && (
            <div>
              <Label className="text-xs">Cc</Label>
              <div className="mt-1">
                <EmailChipsInput value={cc} onChange={setCc} placeholder="Cc recipients…" testIdPrefix="compose-cc" />
              </div>
            </div>
          )}
          {showBcc && (
            <div>
              <Label className="text-xs">Bcc</Label>
              <div className="mt-1">
                <EmailChipsInput value={bcc} onChange={setBcc} placeholder="Bcc recipients…" testIdPrefix="compose-bcc" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="mt-1"
              data-testid="compose-subject"
            />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder="Write your message…"
              className="mt-1 min-h-[180px] resize-y"
              data-testid="compose-body"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={send}
            disabled={sending || inboxes.length === 0 || to.length === 0 || !body.trim()}
            className="bg-[#0033CC] hover:bg-[#002299] text-white"
            data-testid="compose-send"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
            {sending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
